// Host achievement definitions and tracking system
import { collection, getDocs, query, where, doc, updateDoc, getDoc, orderBy, limit } from 'firebase/firestore';

export const HOST_ACHIEVEMENTS = {
  // Founder's Club - First 100 hosts
  HOST_FOUNDERS_CLUB: {
    id: 'host_founders_club',
    name: "Host Founder's Club",
    description: 'One of the first 100 hosts to join Trivia Plug',
    icon: '🎤',
    category: 'exclusive',
    requirement: 'Be among the first 100 hosts to register'
  },

  // Founder Badge - Special for you
  HOST_FOUNDER: {
    id: 'host_founder',
    name: 'Host Founder',
    description: 'The creator of Trivia Plug hosting system',
    icon: '🌟',
    category: 'exclusive',
    requirement: 'Platform founder'
  },

  // Company Founder Badge - Most prestigious
  COMPANY_FOUNDER: {
    id: 'company_founder',
    name: 'Company Founder',
    description: 'The founder and CEO of Trivia Plug',
    icon: '👑',
    category: 'exclusive',
    requirement: 'Company founder'
  },

  // Hosting Experience Milestones
  HOST_ROOKIE: {
    id: 'host_rookie',
    name: 'Host Rookie',
    description: 'Host your first 5 games',
    icon: '🎮',
    category: 'milestone',
    requirement: 'Games hosted >= 5'
  },
  HOST_REGULAR: {
    id: 'host_regular',
    name: 'Regular Host',
    description: 'Host 25 games',
    icon: '🎯',
    category: 'milestone',
    requirement: 'Games hosted >= 25'
  },
  HOST_VETERAN: {
    id: 'host_veteran',
    name: 'Veteran Host',
    description: 'Host 50 games',
    icon: '🎖️',
    category: 'milestone',
    requirement: 'Games hosted >= 50'
  },
  HOST_LEGEND: {
    id: 'host_legend',
    name: 'Host Legend',
    description: 'Host 100 games',
    icon: '🏅',
    category: 'milestone',
    requirement: 'Games hosted >= 100'
  },

  // Rating Achievements
  HOST_STAR: {
    id: 'host_star',
    name: 'Host Star',
    description: 'Achieve a 4.0+ average rating',
    icon: '⭐',
    category: 'performance',
    requirement: 'Average rating >= 4.0'
  },
  HOST_SUPERSTAR: {
    id: 'host_superstar',
    name: 'Host Superstar',
    description: 'Achieve a 4.5+ average rating with 10+ games',
    icon: '🌟',
    category: 'performance',
    requirement: 'Average rating >= 4.5 AND games >= 10'
  },
  HOST_PERFECT: {
    id: 'host_perfect',
    name: 'Perfect Host',
    description: 'Achieve a 5.0 average rating with 20+ games',
    icon: '👑',
    category: 'performance',
    requirement: 'Average rating >= 5.0 AND games >= 20'
  },

  // Venue Achievements
  VENUE_MASTER: {
    id: 'venue_master',
    name: 'Venue Master',
    description: 'Host games at 5 different venues',
    icon: '🏢',
    category: 'venue',
    requirement: 'Host at 5+ different venues'
  },
  VENUE_EXPLORER: {
    id: 'venue_explorer',
    name: 'Venue Explorer',
    description: 'Host games at 10 different venues',
    icon: '🗺️',
    category: 'venue',
    requirement: 'Host at 10+ different venues'
  },

  // Category Achievements
  CATEGORY_MASTER: {
    id: 'category_master',
    name: 'Category Master',
    description: 'Host games in 5 different categories',
    icon: '📚',
    category: 'category',
    requirement: 'Host in 5+ different categories'
  },
  CATEGORY_EXPERT: {
    id: 'category_expert',
    name: 'Category Expert',
    description: 'Host games in 10 different categories',
    icon: '🎓',
    category: 'category',
    requirement: 'Host in 10+ different categories'
  },

  // Social Media Achievements
  SOCIAL_HOST: {
    id: 'social_host',
    name: 'Social Host',
    description: 'Connect 3 social media accounts',
    icon: '📱',
    category: 'social',
    requirement: 'Connect 3+ social media accounts'
  },
  BRAND_BUILDER: {
    id: 'brand_builder',
    name: 'Brand Builder',
    description: 'Connect all 6 social media platforms',
    icon: '🏷️',
    category: 'social',
    requirement: 'Connect all social media platforms'
  },

  // Booking Achievements
  BOOKING_MASTER: {
    id: 'booking_master',
    name: 'Booking Master',
    description: 'Receive 10 booking inquiries',
    icon: '📞',
    category: 'booking',
    requirement: 'Receive 10+ booking inquiries'
  },
  POPULAR_HOST: {
    id: 'popular_host',
    name: 'Popular Host',
    description: 'Receive 50 booking inquiries',
    icon: '🔥',
    category: 'booking',
    requirement: 'Receive 50+ booking inquiries'
  },

  // Consistency Achievements
  CONSISTENT_HOST: {
    id: 'consistent_host',
    name: 'Consistent Host',
    description: 'Host games for 30 consecutive days',
    icon: '📅',
    category: 'consistency',
    requirement: 'Host for 30+ consecutive days'
  },
  DEDICATED_HOST: {
    id: 'dedicated_host',
    name: 'Dedicated Host',
    description: 'Host games for 90 consecutive days',
    icon: '💪',
    category: 'consistency',
    requirement: 'Host for 90+ consecutive days'
  },

  // Team Interaction Achievements
  TEAM_FAVORITE: {
    id: 'team_favorite',
    name: 'Team Favorite',
    description: 'Host games for 20 different teams',
    icon: '👥',
    category: 'interaction',
    requirement: 'Host for 20+ different teams'
  },
  COMMUNITY_BUILDER: {
    id: 'community_builder',
    name: 'Community Builder',
    description: 'Host games for 50 different teams',
    icon: '🏘️',
    category: 'interaction',
    requirement: 'Host for 50+ different teams'
  }
};

// Check if host qualifies for Founder's Club (first 100 hosts)
export const checkHostFoundersClub = async (hostId, db) => {
  try {
    // Get all hosts ordered by creation date
    const hostsQuery = query(
      collection(db, 'users'),
      orderBy('createdAt', 'asc'),
      limit(100)
    );
    const hostsSnapshot = await getDocs(hostsQuery);
    
    // Check if this host is in the first 100
    const isFounder = hostsSnapshot.docs.some(doc => doc.id === hostId);
    return isFounder;
  } catch (error) {
    console.error('Error checking Host Founder\'s Club:', error);
    return false;
  }
};

// Check if user is the founder (you can set your user ID here)
export const checkHostFounder = (userId) => {
  // Replace 'your-actual-user-id-here' with your Firebase User UID
  const FOUNDER_USER_ID = '25ASAtsXhQYFIR3wOX2m2TTaJxD3';
  return userId === FOUNDER_USER_ID;
};

// Check if user is the company founder (CEO) - Host side
export const checkCompanyFounder = (userId, userEmail) => {
  // Check for the host-side CEO email
  const HOST_CEO_EMAIL = 'patrickbrianmoore@outlook.com';
  return userEmail === HOST_CEO_EMAIL;
};

// Check and award achievements based on host data
export const checkHostAchievements = (hostData) => {
  const newAchievements = [];
  const currentAchievements = hostData.achievements || [];

  // Game count achievements
  if (hostData.gamesHosted >= 5 && !currentAchievements.includes('host_rookie')) {
    newAchievements.push('host_rookie');
  }
  if (hostData.gamesHosted >= 25 && !currentAchievements.includes('host_regular')) {
    newAchievements.push('host_regular');
  }
  if (hostData.gamesHosted >= 50 && !currentAchievements.includes('host_veteran')) {
    newAchievements.push('host_veteran');
  }
  if (hostData.gamesHosted >= 100 && !currentAchievements.includes('host_legend')) {
    newAchievements.push('host_legend');
  }

  // Rating achievements
  if (hostData.averageRating >= 4.0 && !currentAchievements.includes('host_star')) {
    newAchievements.push('host_star');
  }
  if (hostData.averageRating >= 4.5 && hostData.gamesHosted >= 10 && !currentAchievements.includes('host_superstar')) {
    newAchievements.push('host_superstar');
  }
  if (hostData.averageRating >= 5.0 && hostData.gamesHosted >= 20 && !currentAchievements.includes('host_perfect')) {
    newAchievements.push('host_perfect');
  }

  // Venue achievements
  if (hostData.venuesHosted >= 5 && !currentAchievements.includes('venue_master')) {
    newAchievements.push('venue_master');
  }
  if (hostData.venuesHosted >= 10 && !currentAchievements.includes('venue_explorer')) {
    newAchievements.push('venue_explorer');
  }

  // Category achievements
  if (hostData.categoriesHosted >= 5 && !currentAchievements.includes('category_master')) {
    newAchievements.push('category_master');
  }
  if (hostData.categoriesHosted >= 10 && !currentAchievements.includes('category_expert')) {
    newAchievements.push('category_expert');
  }

  // Social media achievements
  const socialMediaCount = Object.values(hostData.socialMedia || {}).filter(url => url.trim()).length;
  if (socialMediaCount >= 3 && !currentAchievements.includes('social_host')) {
    newAchievements.push('social_host');
  }
  if (socialMediaCount >= 6 && !currentAchievements.includes('brand_builder')) {
    newAchievements.push('brand_builder');
  }

  // Booking achievements
  if (hostData.bookingInquiries >= 10 && !currentAchievements.includes('booking_master')) {
    newAchievements.push('booking_master');
  }
  if (hostData.bookingInquiries >= 50 && !currentAchievements.includes('popular_host')) {
    newAchievements.push('popular_host');
  }

  // Consistency achievements
  if (hostData.consecutiveDays >= 30 && !currentAchievements.includes('consistent_host')) {
    newAchievements.push('consistent_host');
  }
  if (hostData.consecutiveDays >= 90 && !currentAchievements.includes('dedicated_host')) {
    newAchievements.push('dedicated_host');
  }

  // Team interaction achievements
  if (hostData.teamsHosted >= 20 && !currentAchievements.includes('team_favorite')) {
    newAchievements.push('team_favorite');
  }
  if (hostData.teamsHosted >= 50 && !currentAchievements.includes('community_builder')) {
    newAchievements.push('community_builder');
  }

  return newAchievements;
};

// Get achievement display info
export const getHostAchievementInfo = (achievementId) => {
  return HOST_ACHIEVEMENTS[achievementId.toUpperCase()] || null;
};

// Get all achievements by category
export const getHostAchievementsByCategory = () => {
  const categories = {};
  
  Object.values(HOST_ACHIEVEMENTS).forEach(achievement => {
    if (!categories[achievement.category]) {
      categories[achievement.category] = [];
    }
    categories[achievement.category].push(achievement);
  });
  
  return categories;
};

// Utility function to manually award company founder badge
export const awardCompanyFounderBadge = async (userId, db) => {
  try {
    // Get current user data
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    const userData = userDoc.data();
    const currentAchievements = userData.achievements || [];
    
    // Check if already has the badge
    if (currentAchievements.includes('company_founder')) {
      console.log('User already has company founder badge');
      return false;
    }
    
    // Add the badge
    const updatedAchievements = [...currentAchievements, 'company_founder'];
    await updateDoc(doc(db, 'users', userId), {
      achievements: updatedAchievements
    });
    
    console.log('Company founder badge awarded successfully');
    return true;
  } catch (error) {
    console.error('Error awarding company founder badge:', error);
    return false;
  }
};

// Utility function to manually award company founder badge by email
export const awardCompanyFounderBadgeByEmail = async (email, db) => {
  try {
    // Find user by email
    const usersQuery = query(collection(db, 'users'), where('email', '==', email));
    const usersSnapshot = await getDocs(usersQuery);
    
    if (usersSnapshot.empty) {
      throw new Error('User not found with that email');
    }
    
    const userDoc = usersSnapshot.docs[0];
    const userData = userDoc.data();
    const currentAchievements = userData.achievements || [];
    
    // Check if already has the badge
    if (currentAchievements.includes('company_founder')) {
      console.log('User already has company founder badge');
      return false;
    }
    
    // Add the badge
    const updatedAchievements = [...currentAchievements, 'company_founder'];
    await updateDoc(doc(db, 'users', userDoc.id), {
      achievements: updatedAchievements
    });
    
    console.log('Company founder badge awarded successfully to', email);
    return true;
  } catch (error) {
    console.error('Error awarding company founder badge:', error);
    return false;
  }
}; 

export const generateHostId = async (db) => {
  try {
    // Use timestamp-based ID to avoid Firestore permissions issues during signup
    // This ensures unique IDs without needing to query all users
    const timestamp = Date.now();
    const randomSuffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const hostId = `${timestamp}${randomSuffix}`;
    
    console.log('Generated host ID:', hostId);
    return hostId;
  } catch (error) {
    console.error('Error generating host ID:', error);
    // Fallback to timestamp-based ID if there's an error
    return Date.now().toString().slice(-8);
  }
};

export const assignHostIdsToExistingHosts = async (db) => {
  try {
    // Query all users with host role who don't have a hostId
    const hostsQuery = query(collection(db, 'users'), where('role', '==', 'host'));
    const hostsSnapshot = await getDocs(hostsQuery);
    
    const hostsWithoutId = [];
    hostsSnapshot.forEach((doc) => {
      const userData = doc.data();
      if (!userData.hostId) {
        hostsWithoutId.push({ id: doc.id, ...userData });
      }
    });
    
    // Sort by creation date to assign IDs in chronological order
    hostsWithoutId.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(0);
      return dateA - dateB;
    });
    
    // Assign timestamp-based host IDs
    for (const host of hostsWithoutId) {
      const timestamp = Date.now();
      const randomSuffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      const hostId = `${timestamp}${randomSuffix}`;
      
      await updateDoc(doc(db, 'users', host.id), {
        hostId: hostId
      });
      console.log(`Assigned Host ID ${hostId} to ${host.email}`);
      
      // Small delay to ensure unique timestamps
      await new Promise(resolve => setTimeout(resolve, 1));
    }
    
    console.log(`Assigned host IDs to ${hostsWithoutId.length} existing hosts`);
    return hostsWithoutId.length;
  } catch (error) {
    console.error('Error assigning host IDs to existing hosts:', error);
    throw error;
  }
}; 